import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Users, Plus, Trash2, Check, Loader2, Mail, RefreshCw, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
/*
 * ⚠️ Les accès d'une carte passent par le module partagé.
 *
 * Ce fichier portait la trace de la divergence qu'il corrige : il déballait la réponse en
 * `response.data?.access || response.data` là où l'écran Clients la prenait telle quelle, et il
 * lisait le motif d'un refus sous `data.message` — une clé que ces routes n'emploient jamais.
 * « Cet email d'accès existe déjà pour ce client » n'était donc jamais affiché.
 */
import {
  lireAccesDUneCarte, rattacherEmail, detacherEmail, changerActivite, relancerInvitation
} from '../../../api/accesCartes';

const ClientAccessModal = ({ isOpen, onClose, client, token, onUpdate }) => {
  const [accessList, setAccessList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [resending, setResending] = useState(null);
  const [error, setError] = useState(null);

  // Charger les accès du client
  useEffect(() => {
    if (!isOpen || !client?.id) return;

    const loadAccess = async () => {
      setLoading(true);
      setError(null);
      try {
        setAccessList(await lireAccesDUneCarte(token, client.id));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadAccess();
  }, [isOpen, client?.id, token]);

  const handleAddAccess = async () => {
    if (!newEmail.trim()) return;

    // Validation email basique
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      setError('Adresse email invalide');
      return;
    }

    setAdding(true);
    setError(null);

    try {
      const cree = await rattacherEmail(token, client.id, newEmail.trim());
      setAccessList(prev => [...prev, cree]);
      setNewEmail('');
      onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleToggleActive = async (accessId, currentStatus) => {
    try {
      await changerActivite(token, client.id, accessId, !currentStatus);

      setAccessList(prev => prev.map(a =>
        a.id === accessId ? { ...a, isActive: !currentStatus } : a
      ));
      onUpdate();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteAccess = async (accessId) => {
    if (!window.confirm('Supprimer cet accès ?')) return;

    try {
      await detacherEmail(token, client.id, accessId);

      setAccessList(prev => prev.filter(a => a.id !== accessId));
      onUpdate();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResendInvitation = async (accessId) => {
    setResending(accessId);
    setError(null);

    try {
      const reponse = await relancerInvitation(token, client.id, accessId);

      // Mettre à jour l'état de l'invitation dans la liste
      setAccessList(prev => prev.map(a =>
        a.id === accessId ? { ...a, invitation: reponse?.invitation ?? a.invitation } : a
      ));
    } catch (err) {
      setError(err.message);
    } finally {
      setResending(null);
    }
  };

  // Helper pour afficher le statut d'invitation
  const getInvitationBadge = (invitation) => {
    if (!invitation) return null;

    const styles = {
      PENDING: {
        bg: '#fef3c7',
        color: '#d97706',
        icon: Clock,
        text: 'En attente'
      },
      ACCEPTED: {
        bg: '#dcfce7',
        color: '#16a34a',
        icon: CheckCircle2,
        text: 'Connecté'
      },
      EXPIRED: {
        bg: '#fee2e2',
        color: '#dc2626',
        icon: AlertCircle,
        text: 'Expiré'
      }
    };

    const config = styles[invitation.status] || styles.PENDING;
    const Icon = config.icon;

    return (
      <span style={{
        padding: '0.25rem 0.5rem',
        borderRadius: '0.25rem',
        fontSize: '0.7rem',
        fontWeight: '500',
        backgroundColor: config.bg,
        color: config.color,
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem'
      }}>
        <Icon size={10} />
        {config.text}
        {invitation.isGoogleEmail && <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>(Google)</span>}
      </span>
    );
  };

  if (!isOpen) return null;

  const modalContentStyle = {
    maxWidth: '560px',
    width: '90vw',
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    margin: 0
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #e5e0d5'
  };

  const titleStyle = {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const subtitleStyle = {
    fontSize: '0.875rem',
    color: '#64748b',
    marginTop: '0.25rem'
  };

  const closeButtonStyle = {
    padding: '0.5rem',
    borderRadius: '0.375rem',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: '#64748b',
    display: 'flex'
  };

  const bodyStyle = {
    padding: '1.5rem',
    flex: 1,
    overflowY: 'auto'
  };

  const addSectionStyle = {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem'
  };

  const inputStyle = {
    flex: 1,
    padding: '0.625rem 0.75rem',
    borderRadius: '0.5rem',
    border: '1px solid #e5e0d5',
    fontSize: '0.875rem',
    outline: 'none'
  };

  const addButtonStyle = {
    padding: '0.625rem 1rem',
    borderRadius: '0.5rem',
    border: 'none',
    backgroundColor: '#1a1a1a',
    color: 'white',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem'
  };

  const accessItemStyle = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#f3efe7',
    borderRadius: '0.5rem',
    marginBottom: '0.5rem'
  };

  const emailStyle = {
    flex: 1,
    fontSize: '0.875rem',
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const statusBadgeStyle = (isActive) => ({
    padding: '0.25rem 0.5rem',
    borderRadius: '0.25rem',
    fontSize: '0.7rem',
    fontWeight: '500',
    backgroundColor: isActive ? '#dcfce7' : '#f3efe7',
    color: isActive ? '#16a34a' : '#64748b',
    cursor: 'pointer',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem'
  });

  const deleteButtonStyle = {
    padding: '0.375rem',
    borderRadius: '0.25rem',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#94a3b8',
    cursor: 'pointer',
    display: 'flex'
  };

  const footerStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e5e0d5',
    backgroundColor: '#f3efe7',
    borderRadius: '0 0 0.75rem 0.75rem',
    margin: 0
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      paddingTop: '5vh',
      zIndex: 9999
    }}>
      <div className="modal-content" style={{
        ...modalContentStyle,
        backgroundColor: 'white',
        borderRadius: '0.75rem',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div>
            <div style={titleStyle}>
              <Users size={20} />
              Accès client
            </div>
            <div style={subtitleStyle}>{client?.name}</div>
          </div>
          <button style={closeButtonStyle} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div style={bodyStyle}>
          <div style={addSectionStyle}>
            <input
              type="email"
              placeholder="email@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              style={inputStyle}
              onKeyPress={(e) => e.key === 'Enter' && handleAddAccess()}
              onFocus={(e) => {
                e.target.style.borderColor = '#1a1a1a';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e0d5';
                e.target.style.boxShadow = 'none';
              }}
            />
            <button
              style={addButtonStyle}
              onClick={handleAddAccess}
              disabled={adding || !newEmail.trim()}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#333333';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1a1a1a';
              }}
            >
              {adding ? (
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Plus size={16} />
              )}
              Ajouter
            </button>
          </div>

          {error && (
            <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#fef2f2', borderRadius: '0.5rem', color: '#dc2626', fontSize: '0.875rem' }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', color: '#64748b' }}>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ marginLeft: '0.5rem' }}>Chargement...</span>
            </div>
          ) : accessList.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
              <Mail size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
              <p>Aucun accès configuré</p>
              <p style={{ fontSize: '0.75rem' }}>Ajoutez un email pour donner accès à ce client</p>
            </div>
          ) : (
            <div>
              {accessList.map(access => (
                <div key={access.id} style={accessItemStyle}>
                  <div style={emailStyle}>
                    <Mail size={14} style={{ color: '#64748b' }} />
                    <span style={{ flex: 1 }}>{access.email}</span>
                  </div>
                  {/* Statut invitation */}
                  {getInvitationBadge(access.invitation)}
                  {/* Bouton Renvoyer pour PENDING et EXPIRED */}
                  {access.invitation && (access.invitation.status === 'PENDING' || access.invitation.status === 'EXPIRED') && (
                    <button
                      style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.7rem',
                        fontWeight: '500',
                        backgroundColor: '#e0f2fe',
                        color: '#0284c7',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                      onClick={() => handleResendInvitation(access.id)}
                      disabled={resending === access.id}
                      title="Renvoyer l'invitation"
                    >
                      {resending === access.id ? (
                        <Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <RefreshCw size={10} />
                      )}
                      Renvoyer
                    </button>
                  )}
                  <button
                    style={statusBadgeStyle(access.isActive)}
                    onClick={() => handleToggleActive(access.id, access.isActive)}
                  >
                    {access.isActive ? (
                      <>
                        <Check size={10} />
                        Actif
                      </>
                    ) : (
                      'Inactif'
                    )}
                  </button>
                  <button
                    style={deleteButtonStyle}
                    onClick={() => handleDeleteAccess(access.id)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#fee2e2';
                      e.currentTarget.style.color = '#dc2626';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#94a3b8';
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={footerStyle}>
          <button
            style={{
              padding: '0.625rem 1.25rem',
              borderRadius: '0.5rem',
              border: '1px solid #e5e0d5',
              backgroundColor: 'white',
              color: '#64748b',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
            onClick={onClose}
          >
            Fermer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ClientAccessModal;
